import { template as template_84bed7bc7f724a01a0362e5d21cf7f3c } from "@ember/template-compiler";
import { buildServiceFacetGroups } from 'client-portal/utils/clinicianSearch/service-facet-groups';
import { commaSeparatedStringsForQueryParam, parseCommaSeparatedStrings } from 'client-portal/utils/clinicianSearch/comma-query-param';
import { cptFlagDescriptors } from 'client-portal/utils/clinicianSearch/service-facet-helpers/flag-descriptors';
import { facetOptionDisabled } from 'client-portal/utils/clinicianSearch/facet-catalog';
import { fn } from '@ember/helper';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import RequestClinicianSearchFiltersFilterRadioLine from '../filters/filter-radio-line';
import RequestClinicianSearchFiltersPanel from '../filters/panel';
import styles from './services.module.scss';
import t from 'ember-intl/helpers/t';
const RADIO_NAME = 'clinician-search-service-filter';
const FILTER_DETAIL = 'booking.clinicianSearch.filterDetail';
export default class RequestClinicianSearchFacetFiltersServices extends Component {
    @service
    intl;
    @service
    store;
    @service
    clinicianSearch;
    styles = styles;
    radioName = RADIO_NAME;
    @tracked
    groups = [];
    @tracked
    loadFailed = false;
    @tracked
    isLoadingGroups = true;
    get selectedCptCodesCsv() {
        return (commaSeparatedStringsForQueryParam(parseCommaSeparatedStrings(this.clinicianSearch.query.cptCodes ?? '')) ?? '');
    }
    get flatOptions() {
        let out = [];
        for (let g of this.groups){
            let heading = g.heading ?? '';
            for (let r of g.rates ?? []){
                let ids = r.allRateIds ?? (r.key ? [
                    r.key
                ] : []);
                let cptCodesCsv = commaSeparatedStringsForQueryParam(ids) ?? '';
                out.push({
                    key: r.key,
                    cptRateIds: ids,
                    cptCodesCsv,
                    docCount: Number(r.docCount ?? 0),
                    description: r.description ?? heading ?? '',
                    duration: r.duration,
                    rate: r.rate,
                    callToBook: r.callToBook,
                    requireCreditCardForNewClients: r.requireCreditCardForNewClients,
                    flagged: r.flagged ?? [],
                    groupBucketIdsCsv: cptCodesCsv
                });
            }
        }
        return out;
    }
    optionPrimaryText = (opt)=>{
        let desc = String(opt.description ?? '').trim();
        return desc || this.intl.t('booking.clinicianSearch.filterDetail.cptGroupFallback');
    };
    optionFlaggedLines = (opt)=>cptFlagDescriptors(opt, opt.flagged ?? []).map((d)=>this.formatFlagLine(d));
    flagLineLeaf = (descriptor)=>{
        switch(descriptor.kind){
            case 'duration':
                return [
                    'cptFlagDurationLine',
                    {
                        minutes: this.intl.t(`${FILTER_DETAIL}.cptDurationMinutes`, {
                            count: descriptor.minutes
                        })
                    }
                ];
            case 'rate':
                return [
                    'cptFlagRateLine',
                    {
                        rate: descriptor.rate
                    }
                ];
            case 'callToBook':
                return [
                    descriptor.yes ? 'cptFlagCallToBookYes' : 'cptFlagCallToBookNo'
                ];
            case 'requireCreditCardForNewClients':
                return [
                    'cptFlagCredit',
                    {
                        state: descriptor.state
                    }
                ];
            /* istanbul ignore next -- descriptors always come from cptFlagDescriptors known kinds */ default:
                return null;
        }
    };
    formatFlagLine = (descriptor)=>{
        let leaf = this.flagLineLeaf(descriptor);
        /* istanbul ignore next -- flagLineLeaf default is unreachable for cptFlagDescriptors output */ if (!leaf) {
            return null;
        }
        let [leafKey, values] = leaf;
        return this.intl.t(`${FILTER_DETAIL}.${leafKey}`, values);
    };
    optionDisabled = (opt)=>facetOptionDisabled(opt.docCount, this.isOptionSelected(opt));
    serviceOptionRowTitle = (opt)=>{
        if (!this.optionDisabled(opt)) {
            return '';
        }
        return this.intl.t('booking.clinicianSearch.filterDetail.disabledFilterOption');
    };
    isOptionSelected = (opt)=>this.selectedCptCodesCsv === opt.cptCodesCsv;
    selectService = (opt, event)=>{
        /* istanbul ignore next -- radios always click as checked and keys are never empty */ if (!event.target.checked || !opt.cptRateIds?.length) {
            return;
        }
        this.clinicianSearch.query.applyFacetFilter('service', opt.cptRateIds);
        this.clinicianSearch.mobile.backFromDetail();
    };
    refreshGroups = async ()=>{
        this.loadFailed = false;
        this.isLoadingGroups = true;
        try {
            let routeParams = {
                ...this.clinicianSearch.query.toRouteParams(),
                cptCodes: null
            };
            let meta = this.clinicianSearch.sendAll.buildFacetsMeta(routeParams);
            this.groups = buildServiceFacetGroups(this.store, meta?.facets?.cptCodeRates ?? [], {
                includeZeroDocCounts: true
            });
        } catch  {
            this.loadFailed = true;
            this.groups = [];
        } finally{
            this.isLoadingGroups = false;
        }
    };
    get panelLoading() {
        return this.isLoadingGroups || this.clinicianSearch.sendAll.isLoading;
    }
    get serviceLoadingAriaLabel() {
        if (this.clinicianSearch.sendAll.isLoading) {
            return this.intl.t('booking.clinicianSearch.filterDetail.filterOptionsLoading');
        }
        return this.intl.t('booking.clinicianSearch.filterDetail.serviceRatesLoading');
    }
    static{
        template_84bed7bc7f724a01a0362e5d21cf7f3c(`
    <RequestClinicianSearchFiltersPanel
      @hideApply={{true}}
      @class="{{this.styles.component}} clinician-search-filter-service-options"
      @facetKey="service"
      @isLoading={{this.panelLoading}}
      @loadingAriaLabel={{this.serviceLoadingAriaLabel}}
      @onLoad={{this.refreshGroups}}
      data-test-clinician-search-filter-service-options
    >
      <p class="filter-section-title" id="clinician-search-service-filter-title">{{t
          "booking.clinicianSearch.filterDetail.servicesFilterPanelTitle"
        }}</p>
      {{#if this.loadFailed}}
        <p class="error">{{t "booking.clinicianSearch.filterDetail.serviceLoadError"}}</p>
      {{/if}}
      <div class="body" role="group" aria-labelledby="clinician-search-service-filter-title">
        <ul class="filter-checkbox-list" role="list">
          {{#each this.flatOptions as |opt|}}
            <RequestClinicianSearchFiltersFilterRadioLine
              @kind="service"
              @optionId={{opt.key}}
              @docCount={{opt.docCount}}
              @name={{this.radioName}}
              @value={{opt.key}}
              @bucketIdsCsv={{opt.groupBucketIdsCsv}}
              @checked={{this.isOptionSelected opt}}
              @disabled={{this.optionDisabled opt}}
              @title={{this.serviceOptionRowTitle opt}}
              @onChange={{fn this.selectService opt}}
            >
              <span class="service-option-primary">{{this.optionPrimaryText opt}}</span>
              <span class="service-option-secondary">
                {{#each (this.optionFlaggedLines opt) as |line|}}
                  <span class="service-option-secondary-line">{{line}}</span>
                {{/each}}
              </span>
            </RequestClinicianSearchFiltersFilterRadioLine>
          {{/each}}
        </ul>
      </div>
    </RequestClinicianSearchFiltersPanel>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
